/**
 * PillarSkillEffect — Earth tree sculptor path evolution (Fork 1).
 *
 * Replaces Sculptor. Targets an enemy within range — a stone column erupts
 * beneath the target, launching them skyward and stunning for 700ms on landing.
 * The pillar persists as a 1×1 impassable formation for 10 seconds. Max 2
 * active pillars; placing a third evicts the oldest (LRU). Starts with low
 * effectiveness (mastery/fizzle governed externally).
 *
 * Mason's +1 maxFormations does NOT carry to Pillar. Mason's enhancesId is
 * 'sculptor' and Pillar is a distinct skill ('pillar'). The cap is baked at 2.
 *
 * Extends InstantSkillEffect; overrides cooldown and update for timer tracking.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Sculptor Path — Pillar
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Terrain config interface
// ---------------------------------------------------------------------------

export interface PillarTerrainConfig {
  readonly type: 'formation';
  readonly position: { x: number; y: number };
  readonly widthTiles: number;
  readonly heightTiles: number;
  readonly hp: number;
  readonly durationMs: number;
  readonly sourceSkillId: string;
  readonly blocksMovement: boolean;
  readonly blocksProjectiles: boolean;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PillarDeps {
  readonly targeting: {
    /**
     * Returns the nearest targetable enemy within range, or null if none.
     * Provides the enemy's ID, current position, and single-tile footprint.
     */
    getTargetedEnemy(rangeTiles: number): {
      enemyId: string;
      position: { x: number; y: number };
      footprintTiles: Array<{ x: number; y: number }>;
    } | null;
  };
  readonly statusEffects: {
    /**
     * Apply a status effect to a target. Returns true if the effect was
     * successfully applied, false if rejected (e.g. immunity window).
     */
    apply(
      effectType: string,
      params: { durationMs: number; sourceSkillId: string },
      targetId: string,
    ): boolean;
  };
  readonly terrain: {
    place(config: PillarTerrainConfig): string;
    remove(terrainId: string): void;
  };
  readonly pathfinding: {
    markImpassable(positions: Array<{ x: number; y: number }>): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PillarConfig {
  /** HP of the placed pillar formation. */
  readonly hp: number;
  /** Duration in ms before the pillar expires. */
  readonly durationMs: number;
  /** Cooldown in ms after each activation (medium tier). */
  readonly cooldownMs: number;
  /** Width of the pillar in tiles. */
  readonly widthTiles: number;
  /** Height of the pillar in tiles. */
  readonly heightTiles: number;
  /** Maximum simultaneously active pillars before LRU eviction. */
  readonly maxActive: number;
  /** Stun duration in ms applied to the targeted enemy on landing. */
  readonly stunDurationMs: number;
  /** Base targeting range in tiles. */
  readonly baseRangeTiles: number;
}

const DEFAULT_CONFIG: PillarConfig = {
  hp: 60,
  durationMs: 10000,
  cooldownMs: 800,       // medium cooldown tier
  widthTiles: 1,
  heightTiles: 1,
  maxActive: 2,
  stunDurationMs: 700,
  baseRangeTiles: 3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PillarSkillEffect extends InstantSkillEffect {
  readonly skillId = 'sculptor_evolved';

  private readonly deps: PillarDeps;
  private readonly config: PillarConfig;

  // Cooldown timer (ms remaining until ready)
  private _cooldownTimer = 0;

  // LRU queue: index 0 is the oldest active pillar
  private _activePillars: string[] = [];

  // Telemetry
  private _pillarsPlacedCount = 0;
  private _evictionCount = 0;
  private _stunsApplied = 0;
  private _stunsRejected = 0;
  private _noTargetCount = 0;
  private _pathfindingCallCount = 0;
  private _lastTerrainConfig: PillarTerrainConfig | null = null;
  private _lastTargetEnemyId: string | null = null;

  constructor(deps: PillarDeps, config: Partial<PillarConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  override get cooldown(): number { return this._cooldownTimer; }

  /** Ordered queue of active pillar terrain IDs (oldest first). */
  get activePillars(): readonly string[] { return this._activePillars; }

  get pillarsPlacedCount(): number { return this._pillarsPlacedCount; }

  get evictionCount(): number { return this._evictionCount; }

  get stunsApplied(): number { return this._stunsApplied; }

  get stunsRejected(): number { return this._stunsRejected; }

  get noTargetCount(): number { return this._noTargetCount; }

  get pathfindingCallCount(): number { return this._pathfindingCallCount; }

  /** The TerrainConfig used for the most recently placed pillar. */
  get lastTerrainConfig(): PillarTerrainConfig | null { return this._lastTerrainConfig; }

  /** The enemy ID targeted by the most recent activation. */
  get lastTargetEnemyId(): string | null { return this._lastTargetEnemyId; }

  // --- InstantSkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownTimer <= 0;
  }

  activate(): boolean {
    if (this._cooldownTimer > 0) return false;

    // Find a target enemy within range
    const target = this.deps.targeting.getTargetedEnemy(this.config.baseRangeTiles);
    if (target === null) {
      this._noTargetCount++;
      return false;
    }

    // LRU eviction: remove oldest pillars until room for one more
    while (this._activePillars.length >= this.config.maxActive) {
      const oldest = this._activePillars.shift()!;
      this.deps.terrain.remove(oldest);
      this._evictionCount++;
    }

    // Place terrain at the enemy's current position
    const terrainConfig: PillarTerrainConfig = {
      type: 'formation',
      position: target.position,
      widthTiles: this.config.widthTiles,
      heightTiles: this.config.heightTiles,
      hp: this.config.hp,
      durationMs: this.config.durationMs,
      blocksMovement: true,
      blocksProjectiles: true,
      sourceSkillId: 'pillar',
    };

    const terrainId = this.deps.terrain.place(terrainConfig);
    this._activePillars.push(terrainId);
    this._lastTerrainConfig = terrainConfig;
    this._lastTargetEnemyId = target.enemyId;
    this._pillarsPlacedCount++;

    // Synchronous pathfinding update
    this.deps.pathfinding.markImpassable(target.footprintTiles);
    this._pathfindingCallCount++;

    // Apply stun to the targeted enemy (may be rejected by immunity)
    const stunApplied = this.deps.statusEffects.apply(
      'stun',
      { durationMs: this.config.stunDurationMs, sourceSkillId: 'pillar' },
      target.enemyId,
    );
    if (stunApplied) {
      this._stunsApplied++;
    } else {
      this._stunsRejected++;
    }

    // Start cooldown
    this._cooldownTimer = this.config.cooldownMs;

    return true;
  }

  override update(delta: number): void {
    if (this._cooldownTimer > 0) {
      this._cooldownTimer -= delta;
      if (this._cooldownTimer < 0) this._cooldownTimer = 0;
    }
  }
}
